import type { Loop } from '@/common/api/types/loops';
import type { ExperimentalSearch } from './searchState';

/**
 * Which run is on screen, layered over the loop the route fixes: `null` means every readable loop
 * merged, a number means that loop specifically (which may or may not be the nav loop), and there is
 * no third state for "the nav loop" — that is just `scope === navLoopId`.
 *
 * This is the one thing this variant adds to Briefing #3's state. Everything else — whether the run
 * action is legal, what the context line says, which loop's objectives apply — is derived from this
 * plus `navLoopId`, never stored separately.
 */
export type LoopScope = number | null;

/** `search.loop` as the URL spells it, turned into the `number | null` the page reasons about. */
export const resolveLoopScope = (
  loopParam: ExperimentalSearch['loop'],
  navLoopId: number,
): LoopScope => {
  if (loopParam === undefined) return navLoopId;
  if (loopParam === 'all') return null;
  return loopParam;
};

/** The inverse of `resolveLoopScope`, for writing `scope` back into the URL. */
export const loopScopeToSearchParam = (
  scope: LoopScope,
  navLoopId: number,
): ExperimentalSearch['loop'] => {
  if (scope === navLoopId) return undefined;
  if (scope === null) return 'all';
  return scope;
};

export const isNavScope = (scope: LoopScope, navLoopId: number): boolean => scope === navLoopId;

/**
 * A scope naming a loop that does not exist, or that the reader cannot see — a mistyped `?loop=`,
 * or a link shared by someone who could see a loop the recipient can't — has nowhere useful to go:
 * the switcher can never resolve its name (it is not in `loops`), so it renders nothing, the banner
 * likewise, and there is no control left standing to recover with. Falls back to the nav loop, but
 * only once the org's loop list has genuinely finished loading — checked against `loopsLoaded`, not
 * against `loops` being non-empty, so a scope that is actually valid is never bounced back to the
 * nav loop just because the list has not arrived yet.
 */
export const validateScope = (
  scope: LoopScope,
  navLoopId: number,
  loops: readonly Loop[],
  loopsLoaded: boolean,
): LoopScope => {
  if (scope === null || scope === navLoopId) return scope;
  if (!loopsLoaded) return scope;
  return loops.some((loop) => loop.id === scope) ? scope : navLoopId;
};
